import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { fmtDuration } from "../lib/duration";
import FeedingRateGauge from "./FeedingRateGauge";
import SubmitReportModal from "./SubmitReportModal";
import PauseModal from "./PauseModal";
import ActivePausePanel from "./ActivePausePanel";
import MessagesPanel from "./MessagesPanel";
import AdhocDelayModal from "./AdhocDelayModal";
import RemarksPanel from "./RemarksPanel";
import UnifiedTimeline from "./UnifiedTimeline";
import VesselPostOpModal from "./VesselPostOpModal";
import BreakdownChats from "./BreakdownChats";
import FlowRateInputModal from "./FlowRateInputModal";
import FlowRateTrend from "./FlowRateTrend";
import CompleteVesselModal from "./CompleteVesselModal";
import ConveyorRoutePanel from "./ConveyorRoutePanel";
import ChangeRouteModal from "./ChangeRouteModal";
import HatchOverlayCard from "./HatchOverlayCard";
import LogHatchTonsModal from "./LogHatchTonsModal";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { toast } from "sonner";
import {
    Boat,
    Calendar,
    Clock,
    Scales,
    User,
    Play,
    CheckCircle,
    Warning,
    TrendUp,
    Plus,
    Hourglass,
    PencilSimple,
    PauseCircle,
    PlayCircle,
    CurrencyDollar,
    ShieldCheck,
    Hash,
    ClipboardText,
    Anchor,
    Path,
} from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

/* ---------- formatters ---------- */
function fmtDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
}
function fmtTime(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false });
}
function fmtDT(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    return `${d.toLocaleDateString()} · ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", hour12: false })}`;
}
function pad(n) {
    return n.toString().padStart(2, "0");
}
function isoToTime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function isoToDate(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function minsToDuration(m) {
    return fmtDuration(m);
}

/**
 * The full reference-match live tracking dashboard UI for a single vessel.
 * Fetches vessel + metrics + reports internally.
 *
 * Action buttons (submit report / mark completed) render only for roles in inputRoles.
 */
export default function LiveTrackingView({ vesselId, compact = false }) {
    const { user } = useAuth();
    const nav = useNavigate();
    const [vessel, setVessel] = useState(null);
    const [metrics, setMetrics] = useState(null);
    const [reports, setReports] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [showFlowRateModal, setShowFlowRateModal] = useState(false);
    const [showLogHatchTonsModal, setShowLogHatchTonsModal] = useState(false);
    const [showChangeRouteModal, setShowChangeRouteModal] = useState(false);
    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [editingReport, setEditingReport] = useState(null);
    const [showPauseModal, setShowPauseModal] = useState(false);
    const [showDelayModal, setShowDelayModal] = useState(false);
    const [now, setNow] = useState(new Date());

    const canInput = user?.role === "supervisor";
    const canReport = ["supervisor", "admin"].includes(user?.role);
    const canFlowRateInput = ["supervisor", "manager", "admin"].includes(user?.role);
    const canSetBudget = ["finance", "admin"].includes(user?.role);
    const canLogSafety = ["safety", "admin"].includes(user?.role);
    const canManageOps = ["admin", "manager", "finance", "safety", "client_liaison", "fleet_manager"].includes(user?.role);
    const canChangeHatch = ["supervisor", "manager", "admin"].includes(user?.role);
    const [showBudgetModal, setShowBudgetModal] = useState(false);
    const [showSafetyModal, setShowSafetyModal] = useState(false);
    const [showOpsDataModal, setShowOpsDataModal] = useState(false);
    const [showHatchModal, setShowHatchModal] = useState(false);
    const [activeAlarms, setActiveAlarms] = useState([]);

    // Pull alerts for THIS vessel so the alarm pulsing on the Portal tile is
    // also visible (and actionable) once the user drills into operations.
    useEffect(() => {
        if (!vesselId) return;
        if (!["admin","manager"].includes(user?.role)) return;
        let cancelled = false;
        const pull = async () => {
            try {
                const r = await api.get("/alerts/active");
                if (cancelled) return;
                const here = (r.data?.alerts || []).filter((a) => a.vessel_id === vesselId && ["breakdown_30m","delay_30m"].includes(a.type));
                setActiveAlarms(here);
            } catch { /* ignore */ }
        };
        pull();
        const id = setInterval(pull, 30000);
        return () => { cancelled = true; clearInterval(id); };
    }, [vesselId, user?.role]);

    const load = async () => {
        try {
            const [v, m, r] = await Promise.all([
                api.get(`/vessels/${vesselId}`),
                api.get(`/vessels/${vesselId}/metrics`),
                api.get(`/vessels/${vesselId}/reports`),
            ]);
            setVessel(v.data);
            setMetrics(m.data);
            setReports(r.data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    useEffect(() => {
        if (!vesselId) return;
        load();
        const t = setInterval(() => setNow(new Date()), 30000);
        const refresh = setInterval(load, 60000);
        return () => {
            clearInterval(t);
            clearInterval(refresh);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vesselId]);

    const complete = () => {
        // Open the completion modal so the supervisor confirms the final
        // total tons loaded — that figure feeds the certificate, history,
        // analytics, and dashboard.
        setShowCompleteModal(true);
    };

    const resume = async () => {
        try {
            await api.post(`/vessels/${vesselId}/resume`);
            toast.success("Vessel loading resumed");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const reportDefaults = useMemo(() => {
        if (!vessel || !metrics) return {};
        const nextDue = new Date(metrics.next_report_due);
        const windowStart = new Date(nextDue.getTime() - 2 * 60 * 60 * 1000);
        return {
            window_date: isoToDate(windowStart.toISOString()),
            window_start: isoToTime(windowStart.toISOString()),
            window_end: isoToTime(nextDue.toISOString()),
        };
    }, [vessel, metrics]);

    if (!vesselId) return null;
    if (!vessel || !metrics) {
        return <div className="p-10 text-slate-500" data-testid="live-loading">Loading…</div>;
    }

    const completion = metrics.eta_iso ? new Date(metrics.eta_iso) : null;
    const totalHoursEst = metrics.avg_flow_rate > 0 ? Math.round(vessel.tonnage / metrics.avg_flow_rate) : null;
    const nextDue = new Date(metrics.next_report_due);
    const overdue = vessel.status === "active" && nextDue < now;

    const opStatus = metrics.operation_status;
    const pauseState = metrics.pause_state;
    const statusColor = opStatus === "RUNNING" ? "green" : opStatus === "DELAYED" ? "amber" : opStatus === "PAUSED" ? "red" : "navy";

    return (
        <>
            {/* Active alarms banner — surfaces what's triggering the alarm pulse on the Portal tile */}
            {activeAlarms.length > 0 && (
                <div className="g-card overflow-hidden mb-4 shadow-sm border-l-4 border-[var(--grindrod-red)]" data-testid="active-alarms-banner">
                    <div className="bg-[var(--grindrod-red)] text-white px-4 py-2 flex items-center gap-2">
                        <Warning size={16} weight="fill" />
                        <div className="font-display font-black uppercase tracking-wide text-sm flex-1">
                            {activeAlarms.length} active alarm{activeAlarms.length === 1 ? "" : "s"} on this vessel
                        </div>
                        <span className="text-[10px] uppercase tracking-wider text-white/70 hidden sm:inline">auto-refresh 30s</span>
                    </div>
                    <div className="divide-y divide-slate-100 bg-white">
                        {activeAlarms.map((a) => (
                            <div key={a.id} className="px-4 py-2 flex items-center gap-3" data-testid={`active-alarm-${a.type}`}>
                                <span className={`g-pill ${a.severity === "critical" ? "g-pill-red" : "g-pill-amber"}`}>{a.severity}</span>
                                <span className="text-[10px] uppercase tracking-wider text-slate-500">{a.type.replace(/_/g, " ")}</span>
                                <span className="text-sm text-slate-700 flex-1">{a.message}</span>
                                <span className="text-[10px] text-slate-400 tabular">{new Date(a.fired_at).toLocaleString("en-GB", { dateStyle: "short", timeStyle: "short" })}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Reference-match title banner */}
            <div className="g-card overflow-hidden mb-6 shadow-sm">
                <div className="bg-[var(--grindrod-navy)] text-white px-5 py-4 md:py-5 flex items-center gap-4 border-b-[3px] border-[var(--grindrod-red)]">
                    <div className="flex-1">
                        <div className="uppercase-label text-[11px] text-slate-300">Live Tracking</div>
                        <div className="font-display font-black text-xl md:text-2xl leading-tight mt-0.5">
                            REAL-TIME VESSEL LOADING OPERATIONS
                        </div>
                    </div>
                    <span className={`g-pill g-pill-${statusColor}`} data-testid="op-status-badge">
                        <span className={opStatus === "RUNNING" ? "live-dot" : ""} /> {opStatus}
                    </span>
                </div>

                {/* Vessel info strip with green left accent bar */}
                <div className="relative">
                    <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-[var(--grindrod-green)]" />
                    <div className="pl-3">
                        <div className="grid grid-cols-2 md:grid-cols-5" data-testid="vessel-info-strip">
                            <InfoCell icon={<User size={18} />} label="Client Name" value={vessel.client_name} />
                            <InfoCell icon={<Boat size={18} />} label="Vessel Name" value={vessel.vessel_name} />
                            <InfoCell icon={<Scales size={18} />} label="Vessel Tonnage" value={`${vessel.tonnage.toLocaleString()}`} unit="tons" />
                            <InfoCell
                                icon={<Calendar size={18} />}
                                label="Vessel Start Date/Time"
                                value={`${fmtDate(vessel.start_datetime)}\n${fmtTime(vessel.start_datetime)}`}
                            />
                            <InfoCell
                                icon={<Hourglass size={18} />}
                                label="Estimated Completion"
                                value={completion ? `${fmtDate(completion.toISOString())}\n${fmtTime(completion.toISOString())}` : "—"}
                                sub={totalHoursEst ? `(Estimated ${totalHoursEst} hours)` : vessel.status === "completed" ? "(Completed)" : ""}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Vessel loading dashboard — per-hatch overlay + KPI strip */}
            <HatchOverlayCard
                vessel={vessel}
                metrics={metrics}
                canConfig={canInput || user?.role === "admin" || user?.role === "manager"}
                onOpenHatchChange={canChangeHatch ? () => setShowHatchModal(true) : undefined}
            />

            {/* Actions - role-aware: supervisors get the full set; managers + admins still see flow-rate input + certificate */}
            {(canInput || canReport || canFlowRateInput) && (
                <div className="flex flex-wrap items-center gap-3 mb-6">
                    {canReport && vessel.status === "active" && (
                        <>
                            <button onClick={() => setShowModal(true)} className="btn-primary inline-flex items-center gap-2" data-testid="open-report-modal-btn">
                                <Plus size={14} /> Generate 2 hourly report
                            </button>
                        </>
                    )}
                    {canFlowRateInput && vessel.status === "active" && (
                        <button onClick={() => setShowFlowRateModal(true)} className="btn-outline inline-flex items-center gap-2" data-testid="open-flow-rate-modal-btn">
                            <TrendUp size={14} /> Update TOB
                        </button>
                    )}
                    {(canInput || user?.role === "admin") && vessel.status === "active" && (
                        <button onClick={() => setShowLogHatchTonsModal(true)} className="btn-outline inline-flex items-center gap-2" data-testid="open-log-hatch-tons-btn">
                            <Anchor size={14} /> Log Hatch Tons
                        </button>
                    )}
                    {canInput && vessel.status === "active" && !pauseState && (
                        <>
                            <button onClick={() => setShowPauseModal(true)} className="btn-outline inline-flex items-center gap-2 !border-[var(--grindrod-red)] !text-[var(--grindrod-red)]" data-testid="pause-vessel-btn">
                                <PauseCircle size={14} weight="fill" /> Pause loading
                            </button>
                            <button onClick={() => setShowDelayModal(true)} className="btn-outline inline-flex items-center gap-2" data-testid="log-delay-btn">
                                <Clock size={14} /> Log delay
                            </button>
                            <button onClick={() => setShowChangeRouteModal(true)} className="btn-outline inline-flex items-center gap-2" data-testid="change-route-btn">
                                <Path size={14} weight="bold" /> Change route
                            </button>
                            <button onClick={complete} className="btn-outline inline-flex items-center gap-2" data-testid="complete-vessel-btn">
                                <CheckCircle size={14} /> Mark completed
                            </button>
                        </>
                    )}
                    {canInput && vessel.status === "active" && pauseState && (
                        <button
                            onClick={resume}
                            className="btn-primary inline-flex items-center gap-2"
                            data-testid="resume-vessel-btn"
                            style={{ background: "var(--grindrod-green)" }}
                        >
                            <PlayCircle size={14} weight="fill" /> Resume loading
                        </button>
                    )}
                    <button onClick={() => nav(`/vessel/${vessel.id}/certificate`)} className="btn-outline inline-flex items-center gap-2" data-testid="view-certificate-btn">
                        Performance certificate
                    </button>
                    {canChangeHatch && (
                        <button onClick={() => setShowHatchModal(true)} className="btn-outline inline-flex items-center gap-2" data-testid="vessel-hatch-btn">
                            <Hash size={14} weight="bold" /> Hatch {vessel.current_hatch ? `H${vessel.current_hatch}` : "—"} · change
                        </button>
                    )}
                    {canSetBudget && (
                        <button onClick={() => setShowBudgetModal(true)} className="btn-outline inline-flex items-center gap-2" data-testid="vessel-budget-btn">
                            <CurrencyDollar size={14} weight="fill" /> Budget &amp; costs
                        </button>
                    )}
                    {canLogSafety && (
                        <button onClick={() => setShowSafetyModal(true)} className="btn-outline inline-flex items-center gap-2 !border-[var(--grindrod-green)] !text-[var(--grindrod-green)]" data-testid="vessel-safety-btn">
                            <ShieldCheck size={14} weight="fill" /> Log safety incident
                        </button>
                    )}
                    {canManageOps && (
                        <button onClick={() => setShowOpsDataModal(true)} className="btn-outline inline-flex items-center gap-2 !border-[#B36B00] !text-[#B36B00]" data-testid="vessel-opsdata-btn">
                            <ClipboardText size={14} weight="fill" /> Manage ops data
                        </button>
                    )}
                    {canInput && overdue && !pauseState && (
                        <span className="g-pill g-pill-red" data-testid="overdue-pill">
                            <Warning size={12} /> Report overdue — due {fmtTime(nextDue.toISOString())}
                        </span>
                    )}
                    {canInput && !overdue && vessel.status === "active" && !pauseState && (
                        <span className="text-xs text-slate-500">
                            Next report due at <span className="tabular font-semibold text-[var(--grindrod-navy)]">{fmtTime(nextDue.toISOString())}</span>
                        </span>
                    )}
                </div>
            )}
            {!canInput && !canReport && !canFlowRateInput && (
                <div className="flex flex-wrap items-center gap-3 mb-6">
                    <button onClick={() => nav(`/vessel/${vessel.id}/certificate`)} className="btn-outline inline-flex items-center gap-2" data-testid="view-certificate-btn">
                        Performance certificate
                    </button>
                    {canChangeHatch && (
                        <button onClick={() => setShowHatchModal(true)} className="btn-outline inline-flex items-center gap-2" data-testid="vessel-hatch-btn">
                            <Hash size={14} weight="bold" /> Hatch {vessel.current_hatch ? `H${vessel.current_hatch}` : "—"} · change
                        </button>
                    )}
                    {canSetBudget && (
                        <button onClick={() => setShowBudgetModal(true)} className="btn-outline inline-flex items-center gap-2" data-testid="vessel-budget-btn">
                            <CurrencyDollar size={14} weight="fill" /> Budget &amp; costs
                        </button>
                    )}
                    {canLogSafety && (
                        <button onClick={() => setShowSafetyModal(true)} className="btn-outline inline-flex items-center gap-2 !border-[var(--grindrod-green)] !text-[var(--grindrod-green)]" data-testid="vessel-safety-btn">
                            <ShieldCheck size={14} weight="fill" /> Log safety incident
                        </button>
                    )}
                    {canManageOps && (
                        <button onClick={() => setShowOpsDataModal(true)} className="btn-outline inline-flex items-center gap-2 !border-[#B36B00] !text-[#B36B00]" data-testid="vessel-opsdata-btn">
                            <ClipboardText size={14} weight="fill" /> Manage ops data
                        </button>
                    )}
                    {overdue && !pauseState && (
                        <span className="g-pill g-pill-red" data-testid="overdue-pill">
                            <Warning size={12} /> Report overdue — due {fmtTime(nextDue.toISOString())}
                        </span>
                    )}
                    {!overdue && vessel.status === "active" && !pauseState && (
                        <span className="text-xs text-slate-500">
                            Next report due at <span className="tabular font-semibold text-[var(--grindrod-navy)]">{fmtTime(nextDue.toISOString())}</span>
                        </span>
                    )}
                </div>
            )}

            {/* Active breakdown editable panel — visible to everyone, editable by supervisor/manager/admin */}
            {pauseState && vessel.status === "active" && !compact && (
                <ActivePausePanel vessel={vessel} pauseState={pauseState} onUpdated={load} />
            )}

            {/* Feeding rate + operation status row */}
            <div className="grid gap-5 md:grid-cols-2 mb-6">
                <div className="g-card overflow-hidden shadow-sm">
                    <div className="g-card-header-dark">
                        <TrendUp size={18} />
                        <div className="font-display font-bold uppercase tracking-wide">Conveyor Feeding Rate</div>
                    </div>
                    <div className="p-6">
                        <FeedingRateGauge value={metrics.current_flow_rate} target={1250} max={1500} label="LIVE FLOW RATE (FROM TOB)" />
                        {metrics.paused_long && (
                            <div
                                className="mt-3 text-center text-[11px] uppercase-label text-[var(--grindrod-red)]"
                                data-testid="paused-flow-zero-note"
                            >
                                Paused · live flow rate set to 0
                            </div>
                        )}
                        {/* Vessel start time — anchors the live calculation */}
                        <div className="mt-3 text-center text-[11px] text-slate-500 space-y-0.5" data-testid="vessel-start-time-note">
                            {vessel.planned_start_datetime && (
                                <div>
                                    <span className="uppercase-label text-slate-400 mr-1.5">Planned</span>
                                    <span className="tabular text-slate-600">{fmtDT(vessel.planned_start_datetime)}</span>
                                </div>
                            )}
                            <div>
                                <span className="uppercase-label text-slate-400 mr-1.5">Actual start</span>
                                <span className="tabular font-semibold text-[var(--grindrod-navy)]">
                                    {fmtDT(vessel.actual_start_datetime || vessel.start_datetime)}
                                </span>
                                <span className="ml-1.5 text-slate-400">
                                    ({(() => {
                                        const ref = vessel.actual_start_datetime || vessel.start_datetime;
                                        const mins = Math.max(0, (Date.now() - new Date(ref).getTime()) / 60000);
                                        return `${fmtDuration(mins)} ago`;
                                    })()})
                                </span>
                            </div>
                            {vessel.planned_start_datetime && vessel.actual_start_datetime && (() => {
                                const lateMin = (new Date(vessel.actual_start_datetime).getTime() - new Date(vessel.planned_start_datetime).getTime()) / 60000;
                                if (lateMin <= 15) return null;
                                return (
                                    <div className="text-[var(--grindrod-red)] font-semibold uppercase-label" data-testid="late-start-badge">
                                        Late start · {fmtDuration(lateMin)}
                                    </div>
                                );
                            })()}
                        </div>
                        {/* Total tons on board — below the live flow rate */}
                        <div className="mt-4 pt-4 border-t border-slate-100 text-center" data-testid="total-tob-block">
                            <div className="uppercase-label text-[var(--grindrod-red)]">Total tons on board</div>
                            <div className="metric-value tabular text-[var(--grindrod-navy)] mt-1" style={{ fontSize: "32px", lineHeight: 1 }} data-testid="total-tob-value">
                                {Math.round(metrics.latest_tob ?? 0).toLocaleString()}
                                <span className="ml-2 text-sm font-semibold text-slate-500 uppercase tracking-wide">t</span>
                            </div>
                            <div className="text-[11px] text-slate-500 mt-1">
                                of <span className="tabular font-semibold">{Math.round(metrics.tonnage ?? 0).toLocaleString()}</span> t · <span className="tabular font-semibold text-[var(--grindrod-green)]">{(metrics.progress_pct ?? 0).toFixed(1)}%</span> loaded
                            </div>
                        </div>
                        <div className="mt-6 pt-5 border-t border-slate-100">
                            <div className={`flex items-center justify-between ${pauseState ? "opacity-50" : ""}`}>
                                <div>
                                    <div className="uppercase-label text-[var(--grindrod-red)]">Overall vessel performance</div>
                                    <div className="text-[10px] text-slate-500 mt-0.5">
                                        TOB ÷ elapsed hours since start
                                        {pauseState && <span className="text-[var(--grindrod-red)] font-semibold"> · TOB average — live rate is 0 while paused</span>}
                                    </div>
                                </div>
                                <div className="text-right">
                                    <div className="metric-value tabular text-3xl text-[var(--grindrod-navy)]" data-testid="overall-rate">
                                        {Math.round(metrics.overall_rate ?? metrics.avg_flow_rate ?? 0).toLocaleString()}
                                    </div>
                                    <div className="text-[10px] uppercase-label text-slate-500">tons / hour</div>
                                </div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-5 pt-4 border-t border-slate-100">
                            <MiniStat label="Reports" value={metrics.reports_count} />
                            <MiniStat label="Elapsed hrs" value={(metrics.total_elapsed_hours ?? 0).toFixed(1)} />
                        </div>
                        {/* 12-hour flow-rate trend sparkline */}
                        <FlowRateTrend vesselId={vesselId} hours={12} />
                    </div>
                </div>

                <div className="g-card overflow-hidden shadow-sm">
                    <div className="g-card-header-dark">
                        <Play size={18} weight="fill" />
                        <div className="font-display font-bold uppercase tracking-wide">Operation Status</div>
                    </div>
                    <div className="p-6">
                        <div
                            className={`rounded-sm p-6 md:p-8 text-center border-2 ${
                                opStatus === "RUNNING"
                                    ? "border-[var(--grindrod-green)] bg-[var(--grindrod-green-bg)]"
                                    : opStatus === "DELAYED"
                                    ? "border-[#FFB300] bg-[#FFF8E1]"
                                    : opStatus === "PAUSED"
                                    ? "border-[var(--grindrod-red)] bg-[#FDE7E9]"
                                    : "border-[var(--grindrod-navy)] bg-slate-50"
                            }`}
                            data-testid="op-status-card"
                        >
                            <div className="flex items-center justify-center gap-4">
                                {opStatus === "RUNNING" ? (
                                    <Play size={54} weight="fill" className="text-[var(--grindrod-green)]" />
                                ) : opStatus === "COMPLETED" ? (
                                    <CheckCircle size={54} weight="fill" className="text-[var(--grindrod-navy)]" />
                                ) : opStatus === "PAUSED" ? (
                                    <PauseCircle size={54} weight="fill" className="text-[var(--grindrod-red)]" />
                                ) : (
                                    <Warning size={54} weight="fill" className="text-[#FFB300]" />
                                )}
                                <div
                                    className="font-display font-black text-4xl md:text-5xl"
                                    style={{
                                        color:
                                            opStatus === "RUNNING"
                                                ? "#00A344"
                                                : opStatus === "DELAYED"
                                                ? "#A67300"
                                                : opStatus === "PAUSED"
                                                ? "var(--grindrod-red)"
                                                : "var(--grindrod-navy)",
                                    }}
                                >
                                    {opStatus}
                                </div>
                            </div>
                            {opStatus === "PAUSED" && pauseState ? (
                                <div className="mt-4 text-left bg-white rounded-sm p-4 border border-[var(--grindrod-red)]/30" data-testid="pause-details">
                                    <div className="grid md:grid-cols-3 gap-3 text-sm">
                                        <div>
                                            <div className="uppercase-label text-[9px]">Reason</div>
                                            <div className="font-semibold text-[var(--grindrod-navy)]">{pauseState.reason}</div>
                                        </div>
                                        <div>
                                            <div className="uppercase-label text-[9px]">ETA to repair</div>
                                            <div className="font-semibold text-[var(--grindrod-navy)] tabular">
                                                {pauseState.eta_minutes != null ? minsToDuration(pauseState.eta_minutes) : "—"}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="uppercase-label text-[9px]">Paused by</div>
                                            <div className="font-semibold text-[var(--grindrod-navy)]">{pauseState.started_by_name}</div>
                                            <div className="text-[10px] text-slate-500">{fmtTime(pauseState.started_at)}</div>
                                        </div>
                                    </div>
                                    <div className="mt-3 pt-3 border-t border-slate-100">
                                        <div className="uppercase-label text-[9px]">Description</div>
                                        <div className="text-sm text-slate-700 whitespace-pre-wrap">{pauseState.description}</div>
                                    </div>
                                </div>
                            ) : (
                                <p className="mt-4 text-sm text-slate-600">
                                    {opStatus === "RUNNING" && "Vessel loading operations are in progress as per schedule."}
                                    {opStatus === "DELAYED" && "Current flow rate is below average. Review latest delays."}
                                    {opStatus === "COMPLETED" && "Loading operation has been completed."}
                                </p>
                            )}
                        </div>

                        <div className="mt-6">
                            <div className="flex items-center justify-between mb-2">
                                <div className="uppercase-label">Tonnage Progress</div>
                                <div className="tabular text-sm font-semibold text-[var(--grindrod-navy)]" data-testid="progress-pct">
                                    {metrics.progress_pct.toFixed(1)}%
                                </div>
                            </div>
                            <div className="h-3 bg-slate-100 rounded-sm overflow-hidden">
                                <div
                                    className="h-full bg-[var(--grindrod-green)] transition-all"
                                    style={{ width: `${Math.min(metrics.progress_pct, 100)}%` }}
                                />
                            </div>
                            <div className="mt-2 flex justify-between text-xs text-slate-500 tabular">
                                <span>{metrics.latest_tob.toLocaleString()} t loaded</span>
                                <span>{metrics.remaining.toLocaleString()} t remaining</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs — Timeline / Reports / Delays / Breakdown Chat / Remarks. Replaces the long scroll. */}
            {!compact && (
                <Tabs defaultValue={pauseState ? "breakdowns" : "timeline"} className="w-full" data-testid="dashboard-tabs">
                    <TabsList className="!h-auto flex flex-nowrap overflow-x-auto sm:grid sm:grid-cols-6 gap-1 mb-4 bg-slate-100 p-1 rounded-sm w-full justify-start sm:justify-stretch" data-testid="dashboard-tabs-list">
                        <TabsTrigger value="timeline" data-testid="tab-timeline" className="flex-shrink-0 sm:flex-shrink data-[state=active]:bg-[var(--grindrod-navy)] data-[state=active]:text-white text-[11px] sm:text-xs uppercase tracking-wide font-display font-bold py-2 whitespace-nowrap">
                            Timeline
                        </TabsTrigger>
                        <TabsTrigger value="reports" data-testid="tab-reports" className="flex-shrink-0 sm:flex-shrink data-[state=active]:bg-[var(--grindrod-navy)] data-[state=active]:text-white text-[11px] sm:text-xs uppercase tracking-wide font-display font-bold py-2 whitespace-nowrap">
                            <span className="sm:hidden">Reports</span>
                            <span className="hidden sm:inline">2-Hourly Reports</span>
                            {reports.length > 0 && (
                                <span className="ml-1.5 inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-full bg-[var(--grindrod-red)] text-white text-[9px] font-bold px-1">
                                    {reports.length}
                                </span>
                            )}
                        </TabsTrigger>
                        <TabsTrigger value="delays" data-testid="tab-delays" className="flex-shrink-0 sm:flex-shrink data-[state=active]:bg-[var(--grindrod-navy)] data-[state=active]:text-white text-[11px] sm:text-xs uppercase tracking-wide font-display font-bold py-2 whitespace-nowrap">
                            Delays
                            {metrics.all_delays.length > 0 && (
                                <span className="ml-1.5 inline-flex min-w-[18px] h-[18px] items-center justify-center rounded-full bg-[var(--grindrod-red)] text-white text-[9px] font-bold px-1">
                                    {metrics.all_delays.length}
                                </span>
                            )}
                        </TabsTrigger>
                        <TabsTrigger value="conveyor" data-testid="tab-conveyor" className="flex-shrink-0 sm:flex-shrink data-[state=active]:bg-[var(--grindrod-navy)] data-[state=active]:text-white text-[11px] sm:text-xs uppercase tracking-wide font-display font-bold py-2 whitespace-nowrap">
                            <span className="sm:hidden">Route</span>
                            <span className="hidden sm:inline">Conveyor Route</span>
                        </TabsTrigger>
                        <TabsTrigger value="breakdowns" data-testid="tab-breakdowns" className="flex-shrink-0 sm:flex-shrink data-[state=active]:bg-[var(--grindrod-navy)] data-[state=active]:text-white text-[11px] sm:text-xs uppercase tracking-wide font-display font-bold py-2 whitespace-nowrap relative">
                            <span className="sm:hidden">Chat</span>
                            <span className="hidden sm:inline">Breakdown Chat</span>
                            {pauseState && (
                                <span className="ml-1.5 inline-flex w-2 h-2 rounded-full bg-[var(--grindrod-red)] animate-pulse" />
                            )}
                        </TabsTrigger>
                        <TabsTrigger value="remarks" data-testid="tab-remarks" className="flex-shrink-0 sm:flex-shrink data-[state=active]:bg-[var(--grindrod-navy)] data-[state=active]:text-white text-[11px] sm:text-xs uppercase tracking-wide font-display font-bold py-2 whitespace-nowrap">
                            <span className="sm:hidden">Remarks</span>
                            <span className="hidden sm:inline">Remarks &amp; Messages</span>
                        </TabsTrigger>
                    </TabsList>

                    {/* Timeline tab */}
                    <TabsContent value="timeline" className="mt-0" data-testid="tab-content-timeline">
                        <UnifiedTimeline vesselId={vesselId} />
                    </TabsContent>

                    {/* Reports tab — flow chart + 2-hourly table */}
                    <TabsContent value="reports" className="mt-0 space-y-5" data-testid="tab-content-reports">
                        {metrics.flow_series.length > 1 && (
                            <div className="g-card shadow-sm">
                                <div className="g-card-header-dark">
                                    <TrendUp size={18} />
                                    <div className="font-display font-bold uppercase tracking-wide">Flow Rate Trend</div>
                                </div>
                                <div className="p-4" style={{ height: 260 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={metrics.flow_series.map((s) => ({ ...s, t: fmtTime(s.window_end) }))}>
                                            <CartesianGrid stroke="#EEF1F5" vertical={false} />
                                            <XAxis dataKey="t" stroke="#64748B" fontSize={12} />
                                            <YAxis stroke="#64748B" fontSize={12} />
                                            <Tooltip contentStyle={{ borderRadius: 2, border: "1px solid #CBD5E1" }} />
                                            <Line type="monotone" dataKey="flow_rate_tph" stroke="#E3000F" strokeWidth={2.5} dot={{ r: 3 }} name="t/h" />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        )}
                        <div className="g-card shadow-sm">
                            <div className="g-card-header-dark">
                                <Clock size={18} />
                                <div className="font-display font-bold uppercase tracking-wide">2-Hourly Reports</div>
                            </div>
                            {reports.length === 0 ? (
                                <div className="p-6 text-sm text-slate-400 text-center" data-testid="reports-empty">No reports yet.</div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="g-table" data-testid="reports-table">
                                        <thead>
                                            <tr>
                                                <th>Window</th>
                                                <th>TOB</th>
                                                <th>Loaded</th>
                                                <th>Effective h</th>
                                                <th>True rate</th>
                                                <th>vs Target</th>
                                                <th>Lost (t)</th>
                                                <th>Delays</th>
                                                <th>Shift</th>
                                                <th>Hatch</th>
                                                <th>Submitted by</th>
                                                {canInput && <th></th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reports.map((r) => {
                                                const eff = Number(r.effective_hours ?? r.window_hours ?? 0);
                                                const trueRate = Number(r.true_flow_rate_tph ?? (eff > 0 ? r.tons_loaded / eff : 0));
                                                const diff = Number(r.target_diff_pct ?? 0);
                                                const lost = Number(r.lost_tonnage_estimate ?? 0);
                                                return (
                                                <React.Fragment key={r.id}>
                                                <tr data-testid={`report-row-${r.id}`}>
                                                    <td className="tabular text-xs">
                                                        {fmtTime(r.window_start)} – {fmtTime(r.window_end)}
                                                        <div className="text-[10px] text-slate-400">{fmtDate(r.window_start)}</div>
                                                    </td>
                                                    <td className="tabular font-semibold">
                                                        {((r.draft_figure != null ? r.draft_figure : (r.effective_tob ?? r.tob)) ?? 0).toLocaleString()}
                                                        {r.draft_figure != null && (
                                                            <span className="ml-1.5 g-pill g-pill-navy" style={{ fontSize: 9, padding: "1px 6px" }}>
                                                                DRAFT
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="tabular">{r.tons_loaded.toLocaleString()}</td>
                                                    <td className="tabular text-xs">{eff.toFixed(2)} h</td>
                                                    <td className="tabular">
                                                        <div className="font-bold">{Math.round(trueRate).toLocaleString()} t/h</div>
                                                        <div className="text-[10px] text-slate-400">tons ÷ effective h</div>
                                                    </td>
                                                    <td className="tabular text-xs">
                                                        <span className={diff >= 0 ? "text-[var(--grindrod-green)] font-bold" : "text-[var(--grindrod-red)] font-bold"}>
                                                            {diff >= 0 ? "+" : ""}{diff.toFixed(0)}%
                                                        </span>
                                                    </td>
                                                    <td className="tabular text-xs text-[var(--grindrod-red)]">
                                                        {lost > 0 ? `~${Math.round(lost).toLocaleString()}` : "—"}
                                                    </td>
                                                    <td>
                                                        {r.delays.length === 0 ? (
                                                            <span className="text-slate-400">None</span>
                                                        ) : (
                                                            <span className="g-pill g-pill-red">{r.delays.length}</span>
                                                        )}
                                                    </td>
                                                    <td className="text-xs text-slate-600 capitalize">{r.shift || "—"}</td>
                                                    <td className="text-xs text-slate-600">{r.hatch_no ? `H${r.hatch_no}` : "—"}</td>
                                                    <td>
                                                        <div className="text-sm font-semibold text-[var(--grindrod-navy)]">{r.submitted_by_name}</div>
                                                        <div className="text-[10px] uppercase-label text-slate-400">{r.submitted_by_position}</div>
                                                        {r.edited_at && (
                                                            <div className="text-[10px] text-[var(--grindrod-red)] mt-0.5" title={`Edited by ${r.edited_by_name || ""} at ${fmtDT(r.edited_at)}`}>
                                                                Edited
                                                            </div>
                                                        )}
                                                    </td>
                                                    {canInput && (
                                                        <td className="text-right pr-3">
                                                            <button
                                                                onClick={() => setEditingReport(r)}
                                                                className="btn-outline !py-1 !px-2 text-xs inline-flex items-center gap-1"
                                                                data-testid={`edit-report-${r.id}`}
                                                                title="Fix this report"
                                                            >
                                                                <PencilSimple size={12} /> Edit
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                                {r.narrative && (
                                                    <tr className="bg-slate-50 border-b border-slate-100" data-testid={`report-narrative-${r.id}`}>
                                                        <td colSpan={canInput ? 12 : 11} className="text-xs text-slate-600 italic px-3 py-1.5">
                                                            <span className="uppercase-label text-slate-400 mr-2">Summary</span>
                                                            {r.narrative}
                                                        </td>
                                                    </tr>
                                                )}
                                                </React.Fragment>
                                            );})}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </TabsContent>

                    {/* Delays tab */}
                    <TabsContent value="delays" className="mt-0" data-testid="tab-content-delays">
                        <div className="g-card shadow-sm">
                            <div className="flex items-stretch">
                                <div className="bg-[var(--grindrod-red)] w-14 flex items-center justify-center">
                                    <Clock size={22} weight="bold" className="text-white" />
                                </div>
                                <div className="flex-1 bg-[var(--grindrod-navy)] text-white px-5 py-4 flex items-center gap-3">
                                    <div className="font-display font-black uppercase tracking-wide text-lg">Delays</div>
                                    <span className="ml-auto text-xs font-semibold">{metrics.all_delays.length} recorded</span>
                                </div>
                            </div>
                            {metrics.all_delays.length === 0 ? (
                                <div className="p-6 text-center text-sm text-slate-400" data-testid="delays-empty">
                                    No delays recorded.
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="g-table" data-testid="delays-table">
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>Duration</th>
                                                <th>Time</th>
                                                <th>Description</th>
                                                <th>Location</th>
                                                <th>Reported by</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {metrics.all_delays.map((d, i) => (
                                                <tr key={d.id || i}>
                                                    <td className="tabular">
                                                        <Calendar size={14} className="inline mr-1.5 text-[var(--grindrod-red)]" />
                                                        {fmtDate(d.start_time)}
                                                    </td>
                                                    <td className="tabular">
                                                        <Clock size={14} className="inline mr-1.5 text-[var(--grindrod-red)]" />
                                                        {minsToDuration(d.duration_minutes)}
                                                        {d.severity === "major" && (
                                                            <span className="ml-2 g-pill g-pill-red" style={{ fontSize: 9, padding: "1px 5px" }}>
                                                                MAJOR
                                                            </span>
                                                        )}
                                                        {d.severity === "minor" && (
                                                            <span className="ml-2 g-pill" style={{ fontSize: 9, padding: "1px 5px", background: "#F1F5F9", color: "#1E293B", border: "1px solid #64748B" }}>
                                                                MINOR
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="tabular">
                                                        {fmtTime(d.start_time)} – {fmtTime(d.end_time)}
                                                    </td>
                                                    <td>
                                                        <div className="text-sm font-semibold text-[var(--grindrod-navy)]">{d.reason}</div>
                                                        {d.actions_taken && <div className="text-xs text-slate-500 mt-0.5">→ {d.actions_taken}</div>}
                                                    </td>
                                                    <td>{d.location || "—"}</td>
                                                    <td className="text-xs text-slate-600">{d.reported_by}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </TabsContent>

                    {/* Breakdown Chats tab */}
                    <TabsContent value="conveyor" className="mt-0" data-testid="tab-content-conveyor">
                        <ConveyorRoutePanel vessel={vessel} canInput={canInput} division={vessel?.division} />
                    </TabsContent>

                    <TabsContent value="breakdowns" className="mt-0" data-testid="tab-content-breakdowns">
                        <BreakdownChats vesselId={vesselId} />
                    </TabsContent>

                    {/* Remarks & Messages tab */}
                    <TabsContent value="remarks" className="mt-0 space-y-5" data-testid="tab-content-remarks">
                        <RemarksPanel vesselId={vesselId} />
                        <MessagesPanel vesselId={vesselId} />
                    </TabsContent>
                </Tabs>
            )}

            {showModal && (
                <SubmitReportModal
                    vessel={vessel}
                    defaults={reportDefaults}
                    onClose={() => setShowModal(false)}
                    onSubmitted={load}
                />
            )}
            {editingReport && (
                <SubmitReportModal
                    vessel={vessel}
                    existingReport={editingReport}
                    onClose={() => setEditingReport(null)}
                    onSubmitted={load}
                />
            )}
            {showPauseModal && (
                <PauseModal
                    vessel={vessel}
                    onClose={() => setShowPauseModal(false)}
                    onSubmitted={load}
                />
            )}
            {showDelayModal && (
                <AdhocDelayModal
                    vessel={vessel}
                    onClose={() => setShowDelayModal(false)}
                    onSubmitted={load}
                />
            )}
            {showFlowRateModal && (
                <FlowRateInputModal
                    vessel={vessel}
                    onClose={() => setShowFlowRateModal(false)}
                    onSubmitted={load}
                />
            )}
            {showCompleteModal && (
                <CompleteVesselModal
                    vessel={vessel}
                    latestTob={metrics?.latest_tob || 0}
                    tonnage={vessel?.tonnage || 0}
                    onClose={() => setShowCompleteModal(false)}
                    onCompleted={() => {
                        setShowCompleteModal(false);
                        load();
                    }}
                />
            )}
            {showBudgetModal && (
                <VesselBudgetModal vessel={vessel} onClose={() => setShowBudgetModal(false)} />
            )}
            {showSafetyModal && (
                <VesselSafetyModal vessel={vessel} onClose={() => setShowSafetyModal(false)} />
            )}
            {showHatchModal && (
                <VesselHatchChangeModal vessel={vessel} onClose={() => setShowHatchModal(false)} onSaved={() => { setShowHatchModal(false); load(); }} />
            )}
            {showLogHatchTonsModal && (
                <LogHatchTonsModal vessel={vessel} onClose={() => setShowLogHatchTonsModal(false)} onLogged={() => { setShowLogHatchTonsModal(false); load(); }} />
            )}
            {showChangeRouteModal && (
                <ChangeRouteModal vessel={vessel} onClose={() => setShowChangeRouteModal(false)} onChanged={load} />
            )}
            {showOpsDataModal && (
                <VesselPostOpModal vessel={vessel} onClose={() => setShowOpsDataModal(false)} onChanged={load} />
            )}
        </>
    );
}

function InfoCell({ icon, label, value, unit, sub }) {
    return (
        <div className="p-4 md:p-5 border-r border-slate-100 last:border-r-0 min-w-0">
            <div className="flex items-center gap-2 text-[var(--grindrod-navy)]">
                <span className="text-slate-500">{icon}</span>
                <div className="uppercase-label text-[10px] text-slate-500 truncate">{label}</div>
            </div>
            <div className="mt-2 whitespace-pre-line font-display font-black text-[var(--grindrod-navy)] text-base md:text-lg lg:text-xl leading-tight break-words">
                {value}
                {unit && <span className="text-xs font-normal text-slate-400 ml-1">{unit}</span>}
            </div>
            {sub && <div className="text-[10px] text-slate-400 mt-1">{sub}</div>}
        </div>
    );
}

function MiniStat({ label, value }) {
    return (
        <div>
            <div className="uppercase-label text-[9px]">{label}</div>
            <div className="tabular font-semibold text-[var(--grindrod-navy)] text-sm mt-0.5">{value}</div>
        </div>
    );
}

/* ===================== VESSEL BUDGET & COSTS MODAL ===================== */
const COST_CATEGORIES = [
    { key: "fuel", label: "Fuel" },
    { key: "breakdowns", label: "Breakdowns" },
    { key: "manpower", label: "Manpower" },
    { key: "claims", label: "Claims" },
    { key: "marine", label: "Marine" },
    { key: "plant_hire", label: "Plant Hire" },
    { key: "maintenance", label: "Maintenance" },
    { key: "other", label: "Other" },
];

function fmtZAR(n) { return `R ${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`; }

function VesselBudgetModal({ vessel, onClose }) {
    const [budget, setBudget] = useState({ total_budget_zar: 0, breakdown: {}, notes: "" });
    const [costs, setCosts] = useState([]);
    const [adding, setAdding] = useState({ category: "fuel", amount_zar: 0, description: "" });
    const [tab, setTab] = useState("budget");

    const reload = async () => {
        try {
            const [b, c] = await Promise.all([
                api.get(`/vessels/${vessel.id}/budget`).catch(() => ({ data: null })),
                api.get(`/vessels/${vessel.id}/costs`).catch(() => ({ data: { items: [] } })),
            ]);
            if (b.data) setBudget({ ...b.data });
            setCosts(c.data?.items || []);
        } catch { /* swallow */ }
    };
    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [vessel.id]);

    const saveBudget = async () => {
        try {
            await api.post(`/vessels/${vessel.id}/budget`, {
                total_budget_zar: parseFloat(budget.total_budget_zar) || 0,
                breakdown: budget.breakdown || {},
                notes: budget.notes || "",
            });
            toast.success("Budget saved");
            await reload();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const addCost = async () => {
        if (!(adding.amount_zar > 0)) return toast.error("Enter an amount");
        try {
            await api.post(`/vessels/${vessel.id}/costs`, adding);
            setAdding({ category: adding.category, amount_zar: 0, description: "" });
            await reload();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const removeCost = async (id) => {
        try {
            await api.delete(`/vessels/${vessel.id}/costs/${id}`);
            await reload();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const totalCost = costs.reduce((a, c) => a + (c.amount_zar || 0), 0);
    const tons = vessel.total_tons_loaded || vessel.latest_tob || 0;
    const cpt = tons ? totalCost / tons : 0;
    const variance = (budget.total_budget_zar || 0) - totalCost;

    return (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="g-card w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="g-card-header-dark flex items-center">
                    <CurrencyDollar size={16} weight="fill" />
                    <div className="font-display font-black uppercase tracking-wide flex-1">Budget &amp; Costs — {vessel.vessel_name}</div>
                    <button onClick={onClose} className="text-white">✕</button>
                </div>

                <div className="grid grid-cols-3 text-xs tabular border-b border-slate-100">
                    <div className="p-3 border-r border-slate-100">
                        <div className="uppercase-label">Budget</div>
                        <div className="font-display font-black text-xl text-[var(--grindrod-navy)]">{fmtZAR(budget.total_budget_zar)}</div>
                    </div>
                    <div className="p-3 border-r border-slate-100">
                        <div className="uppercase-label">Actual costs</div>
                        <div className="font-display font-black text-xl text-[var(--grindrod-navy)]">{fmtZAR(totalCost)}</div>
                    </div>
                    <div className="p-3">
                        <div className="uppercase-label">Variance · R/ton</div>
                        <div className={`font-display font-black text-xl ${variance >= 0 ? "text-[var(--grindrod-green)]" : "text-[var(--grindrod-red)]"}`}>
                            {fmtZAR(Math.abs(variance))} · <span className="text-base">{fmtZAR(cpt)}/t</span>
                        </div>
                    </div>
                </div>

                <div className="flex border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide font-display font-bold">
                    <button onClick={() => setTab("budget")} className={`px-4 py-2 ${tab === "budget" ? "bg-white text-[var(--grindrod-navy)] border-t-2 border-[var(--grindrod-navy)]" : "text-slate-500"}`} data-testid="budget-tab-budget">Budget</button>
                    <button onClick={() => setTab("costs")} className={`px-4 py-2 ${tab === "costs" ? "bg-white text-[var(--grindrod-navy)] border-t-2 border-[var(--grindrod-navy)]" : "text-slate-500"}`} data-testid="budget-tab-costs">Actual costs ({costs.length})</button>
                </div>

                {tab === "budget" ? (
                    <div className="p-4 space-y-3 text-sm">
                        <div>
                            <label className="uppercase-label block mb-1">Total Budget (ZAR)</label>
                            <input type="number" min="0" step="1000" className="g-input tabular font-display font-bold text-lg"
                                   value={budget.total_budget_zar || 0}
                                   onChange={(e) => setBudget({ ...budget, total_budget_zar: e.target.value })}
                                   data-testid="vessel-budget-total" />
                        </div>
                        <div>
                            <div className="uppercase-label mb-1">Breakdown (optional)</div>
                            <div className="grid grid-cols-2 gap-2">
                                {COST_CATEGORIES.map((c) => (
                                    <div key={c.key}>
                                        <label className="text-[10px] text-slate-500 block">{c.label}</label>
                                        <input type="number" min="0" step="1000" className="g-input tabular !py-1.5 text-xs"
                                               value={budget.breakdown?.[c.key] || ""}
                                               onChange={(e) => setBudget({ ...budget, breakdown: { ...(budget.breakdown || {}), [c.key]: parseFloat(e.target.value) || 0 } })}
                                               data-testid={`vessel-budget-cat-${c.key}`} />
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div><label className="uppercase-label block mb-1">Notes</label>
                            <input className="g-input" value={budget.notes || ""} onChange={(e) => setBudget({ ...budget, notes: e.target.value })} />
                        </div>
                        <div className="flex justify-end gap-2 pt-2">
                            <button onClick={onClose} className="btn-outline">Close</button>
                            <button onClick={saveBudget} className="btn-primary" data-testid="vessel-budget-submit">Save budget</button>
                        </div>
                    </div>
                ) : (
                    <div className="p-4 space-y-3 text-sm">
                        <div className="grid grid-cols-[1fr_120px_80px] gap-2 items-end">
                            <div>
                                <label className="text-[10px] text-slate-500 block">Category</label>
                                <select className="g-input !py-1.5 text-xs" value={adding.category} onChange={(e) => setAdding({ ...adding, category: e.target.value })} data-testid="vessel-cost-cat">
                                    {COST_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-[10px] text-slate-500 block">Amount (ZAR)</label>
                                <input type="number" min="0" step="100" className="g-input tabular !py-1.5 text-xs"
                                       value={adding.amount_zar} onChange={(e) => setAdding({ ...adding, amount_zar: parseFloat(e.target.value) || 0 })}
                                       data-testid="vessel-cost-amount" />
                            </div>
                            <button onClick={addCost} className="btn-primary !py-1.5 text-xs" data-testid="vessel-cost-add">Add</button>
                        </div>
                        <input className="g-input !py-1.5 text-xs" placeholder="Description (optional)" value={adding.description} onChange={(e) => setAdding({ ...adding, description: e.target.value })} />

                        <div className="border border-slate-200 rounded-sm overflow-hidden">
                            <table className="w-full text-xs tabular">
                                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                                    <tr><th className="text-left p-2">Category</th><th className="text-left p-2">Description</th><th className="text-right p-2">Amount</th><th className="w-8"></th></tr>
                                </thead>
                                <tbody>
                                    {costs.length === 0 && <tr><td colSpan={4} className="text-center text-slate-400 p-4">No costs logged.</td></tr>}
                                    {costs.map((c) => (
                                        <tr key={c.id} className="border-t border-slate-100">
                                            <td className="p-2 capitalize">{c.category.replace("_", " ")}</td>
                                            <td className="p-2 text-slate-600">{c.description || "—"}</td>
                                            <td className="p-2 text-right font-semibold">{fmtZAR(c.amount_zar)}</td>
                                            <td className="p-2"><button onClick={() => removeCost(c.id)} className="text-slate-400 hover:text-[var(--grindrod-red)]">✕</button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

/* ===================== VESSEL HATCH CHANGE MODAL ===================== */
function VesselHatchChangeModal({ vessel, onClose, onSaved }) {
    const [hatchNo, setHatchNo] = useState((vessel.current_hatch || 1) + 1);
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);
    const [history, setHistory] = useState([]);

    useEffect(() => {
        api.get(`/vessels/${vessel.id}/hatch-history`).then((r) => setHistory(r.data?.history || [])).catch(() => {});
    }, [vessel.id]);

    const submit = async () => {
        const hn = parseInt(hatchNo);
        if (!hn || hn < 1 || hn > 20) return toast.error("Enter a hatch number between 1 and 20");
        if (hn === vessel.current_hatch) return toast.error("Already loading on that hatch");
        setSaving(true);
        try {
            await api.post(`/vessels/${vessel.id}/hatch-change`, { hatch_no: hn, notes });
            toast.success(`Moved to hatch H${hn}`);
            onSaved?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="g-card w-full max-w-md" onClick={(e) => e.stopPropagation()}>
                <div className="g-card-header-dark flex items-center">
                    <Hash size={16} weight="bold" />
                    <div className="font-display font-black uppercase tracking-wide flex-1">Hatch change — {vessel.vessel_name}</div>
                    <button onClick={onClose} className="text-white">✕</button>
                </div>
                <div className="p-4 space-y-3 text-sm">
                    <div className="bg-slate-50 rounded-sm p-2.5 text-xs flex justify-between">
                        <span>Currently loading on</span>
                        <span className="font-display font-black text-[var(--grindrod-navy)]">
                            {vessel.current_hatch ? `H${vessel.current_hatch}` : "(not set)"}
                        </span>
                    </div>
                    <div>
                        <label className="uppercase-label block mb-1">Move to hatch *</label>
                        <div className="flex items-center gap-2">
                            <span className="uppercase-label text-slate-500 text-[10px] tabular w-4 text-center">H</span>
                            <input type="number" min="1" max="20" className="g-input tabular w-24" value={hatchNo}
                                   onChange={(e) => setHatchNo(e.target.value)} data-testid="hatch-change-no" />
                        </div>
                    </div>
                    <div>
                        <label className="uppercase-label block mb-1">Notes (optional)</label>
                        <input className="g-input" value={notes} onChange={(e) => setNotes(e.target.value)}
                               placeholder="e.g. Hatch 1 sealed, ready for survey" data-testid="hatch-change-notes" />
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                        <button onClick={onClose} className="btn-outline">Cancel</button>
                        <button onClick={submit} disabled={saving} className="btn-primary" data-testid="hatch-change-submit">
                            {saving ? "Saving…" : "Confirm change"}
                        </button>
                    </div>

                    {history.length > 0 && (
                        <div className="border-t border-slate-200 pt-3">
                            <div className="uppercase-label mb-2">History</div>
                            <ol className="space-y-1.5 text-xs">
                                {history.slice().reverse().map((h, i) => (
                                    <li key={h.id || `${h.hatch_no}-${h.started_at}-${i}`} className="flex items-start gap-2">
                                        <span className="font-display font-bold text-[var(--grindrod-navy)] w-10">H{h.hatch_no}</span>
                                        <span className="text-slate-500">{new Date(h.started_at).toLocaleString()}</span>
                                        <span className="text-slate-600 truncate">{h.notes || (h.previous_hatch ? `from H${h.previous_hatch}` : "started here")}</span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
function VesselSafetyModal({ vessel, onClose }) {
    const [f, setF] = useState({        incident_type: "near_miss",
        description: "",
        hours_lost: 0,
        operation: "rb_vessel",
        vessel_id: vessel.id,
    });
    const [incidents, setIncidents] = useState([]);

    const load = async () => {
        try {
            const r = await api.get("/safety/incidents", { params: { vessel_id: vessel.id } });
            setIncidents(r.data || []);
        } catch { /* swallow */ }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [vessel.id]);

    const submit = async () => {
        if (!f.description.trim()) return toast.error("Description required");
        try {
            await api.post("/safety/incidents", f);
            toast.success("Safety incident logged");
            setF({ ...f, description: "", hours_lost: 0 });
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="g-card w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="g-card-header-dark flex items-center" style={{ background: "var(--grindrod-green)" }}>
                    <ShieldCheck size={16} weight="fill" />
                    <div className="font-display font-black uppercase tracking-wide flex-1">Safety Incident — {vessel.vessel_name}</div>
                    <button onClick={onClose} className="text-white">✕</button>
                </div>
                <div className="p-4 space-y-3 text-sm">
                    <div><label className="uppercase-label block mb-1">Type</label>
                        <select className="g-input" value={f.incident_type} onChange={(e) => setF({ ...f, incident_type: e.target.value })} data-testid="vessel-safety-type">
                            <option value="near_miss">Near miss</option>
                            <option value="first_aid">First aid</option>
                            <option value="medical">Medical treatment</option>
                            <option value="lost_time">Lost time injury (LTIFR)</option>
                            <option value="property_damage">Property damage</option>
                        </select>
                    </div>
                    <div><label className="uppercase-label block mb-1">Description *</label>
                        <textarea rows={3} className="g-input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="vessel-safety-desc" placeholder="What happened, where, and who was involved" />
                    </div>
                    <div><label className="uppercase-label block mb-1">Hours lost</label>
                        <input type="number" min="0" step="0.5" className="g-input tabular" value={f.hours_lost} onChange={(e) => setF({ ...f, hours_lost: parseFloat(e.target.value) || 0 })} />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <button onClick={onClose} className="btn-outline">Close</button>
                        <button onClick={submit} className="btn-primary" data-testid="vessel-safety-submit">Log incident</button>
                    </div>

                    {incidents.length > 0 && (
                        <div className="border-t border-slate-200 pt-3">
                            <div className="uppercase-label mb-2">Incidents for this vessel</div>
                            <ul className="space-y-2 text-xs">
                                {incidents.map((i) => (
                                    <li key={i.id} className="bg-slate-50 rounded-sm p-2">
                                        <div className="flex justify-between">
                                            <span className="font-semibold capitalize">{i.incident_type.replace("_", " ")}</span>
                                            <span className="text-slate-500">{new Date(i.occurred_at).toLocaleString()}</span>
                                        </div>
                                        <div className="text-slate-700 mt-0.5">{i.description}</div>
                                        <div className="text-slate-400 text-[10px] mt-0.5">{i.logged_by} {i.hours_lost ? `· ${i.hours_lost}h lost` : ""}</div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
